import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { includes, path, pathOr } from 'ramda';
import { string, func, shape, object } from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { getPrices } from '@swimmy_/helpers';

import { propTypes } from '../../util/types';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { DAYS_CONSIDERED_NEW_LISTING, getDaysSinceCreation } from '../../util/listingHelpers';
import defaultListingImg from '../../assets/default-img-listing/default-img-listing.jpg';
import defaultListingImgCrop from '../../assets/default-img-listing/landscape-crop.webp';
import defaultListingImgCrop2 from '../../assets/default-img-listing/landscape-cropx2.webp';
import { isBookingTooLastMinuteForListing } from '../../util/booking';
import { getMinTotalAmountPerBookingWithCustomerFee } from '../../util/pricing';
import {
  FavoritesButton,
  ImageCarousel,
  InstantBookingBadge,
  LastMinuteBadge,
  NamedLink,
  ResponsiveImage,
  SuperhostBadge,
} from '../../components';

import GeolocationInfo from './GeolocationInfo/GeolocationInfo';
import ListingPrice from './ListingPrice/ListingPrice';
import ReviewsInfo from './ReviewsInfo/ReviewsInfo';
import css from './ListingCard.css';
import {isNilOrEmpty} from "../../util/validators";
import {MIN_TOTAL_FOR_PROPOSAL} from "../../marketplace-custom-config";

export const ListingCardComponent = (props) => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    searchParams,
    setActiveListing,
    to,
    myFavorites,
    myCart,
  } = props;
  const { bookingDate } = searchParams;
  const startTime = pathOr(undefined, ['startTime'], searchParams);
  const currentListing = ensureListing(listing);
  const listingUuid = currentListing.id.uuid;
  const isSelected = !isNilOrEmpty(myCart) && listingUuid && includes(listingUuid, myCart);
  const listingTitle = pathOr('', ['attributes', 'title'], currentListing);
  const slug = createSlug(listingTitle);
  const createdAt = path(['attributes', 'createdAt'], currentListing);
  const isFavorites = includes(listingUuid, myFavorites);
  const isHostedBySuperHost = path(['attributes', 'publicData', 'isHostedBySuperHost'], listing);
  const instantBookingFrom = path(
    ['attributes', 'publicData', 'instantBookingFrom'],
    currentListing
  );
  const isInstantBookingListing = !!instantBookingFrom;
  const minHoursBeforeBookingRequest = path(
    ['attributes', 'publicData', 'minHoursBeforeBookingRequest'],
    currentListing
  );
  const parsedMinHoursBeforeBookingRequest = parseInt(minHoursBeforeBookingRequest, 10);
  const minHoursBeforeBookingRequestAsNumber =
    Number.isFinite(parsedMinHoursBeforeBookingRequest) && parsedMinHoursBeforeBookingRequest > 0
      ? parsedMinHoursBeforeBookingRequest
      : 1;
  const showLastMinuteBadge =
    !isInstantBookingListing &&
    isBookingTooLastMinuteForListing(bookingDate, startTime, minHoursBeforeBookingRequestAsNumber);
  const reviewsInfo = pathOr({}, ['attributes', 'publicData', 'reviewsInfo'], currentListing);
  const isNewListing = getDaysSinceCreation(createdAt) <= DAYS_CONSIDERED_NEW_LISTING;
  const showReviewsInfo = isNewListing || reviewsInfo.totalReviews >= 1;
  const defaultListingImgVariants = `${defaultListingImgCrop} 400w, ${defaultListingImgCrop2} 800w`;
  const classes = classNames(rootClassName || css.root, className, {
    [css.unavailable]: showLastMinuteBadge,
  });
  const favoritesButtonClasses = classNames(
    css.favoritesButton,
    isFavorites ? css.removeFavoritesButton : css.addFavoritesButton
  );
  const numberOfPeople = pathOr(0, ['adults'], searchParams);
  const priceMaybe = pathOr(undefined, ['attributes', 'price', 'amount'], listing);
  if (!priceMaybe) {
    console.log('no price', listing.id.uuid, priceMaybe);
    return null;
  }

  const speculativePrices =
    searchParams && priceMaybe
      ? getPrices({ ...listing.attributes, uuid: listing.id.uuid }, searchParams)
      : {};
  const totalPrice = pathOr(0, ['secondaryPrice', 'amount'], speculativePrices)
  const makeOfferButton = numberOfPeople >= 10 && totalPrice >= MIN_TOTAL_FOR_PROPOSAL && (
    <NamedLink
      className={classNames(css.actionTag, css.smallFontSize)}
      name="ListingPage"
      params={{id: listingUuid, slug}}
      to={{state: {showOffer: true}}}
    >
      <FormattedMessage id="ListingCard.makeOffer"/>
    </NamedLink>
  );
  const getCustomerTotalNotEnoughForThisListing = () => {
    const minTotalAmountPerBooking = pathOr(
      undefined,
      ['attributes', 'publicData', 'minTotalAmountPerBooking'],
      listing
    );
    if (!minTotalAmountPerBooking) return false;

    const adultsCount = pathOr(0, ['adults'], searchParams) || 0;
    const childrenCount = pathOr(0, ['children'], searchParams) || 0;
    const infantsCount = pathOr(0, ['infants'], searchParams) || 0;
    const guestCount = adultsCount + childrenCount + infantsCount;

    const minCustomerPayinCents = getMinTotalAmountPerBookingWithCustomerFee(
      minTotalAmountPerBooking,
      guestCount
    );
    if (!minCustomerPayinCents) return false;

    return totalPrice && minCustomerPayinCents && totalPrice < minCustomerPayinCents;
  }
;
  const customerTotalNotEnoughForThisListing = getCustomerTotalNotEnoughForThisListing();
  if (customerTotalNotEnoughForThisListing) {
    return null;
  }

  return (
    <div className={classes}>
      <NamedLink to={to} name="ListingPage" params={{ id: listingUuid, slug }}>
        <div
          className={css.threeToTwoWrapper}
          onMouseEnter={() => setActiveListing(currentListing.id)}
          onMouseLeave={() => setActiveListing(null)}
        >
          <div className={css.aspectWrapper}>
            {currentListing.images === 0 ? (
              <img
                src={defaultListingImg}
                alt={listingTitle}
                className={css.rootForImage}
                srcSet={defaultListingImgVariants}
              />
            ) : (
              <ImageCarousel
                rootClassName={css.rootForImage}
                images={currentListing.images}
                isFullscreen={false}
                isSelected={isSelected}
              />
            )}
          </div>
        </div>
      </NamedLink>
      <NamedLink className={css.link} to={to} name="ListingPage" params={{ id: listingUuid, slug }}>
        <div className={css.info}>
          <div className={css.titleAndReviewWrapper}>
            <span className={css.title}>{listingTitle}</span>
            {showReviewsInfo && (
              <ReviewsInfo
                className={css.reviewsInfo}
                intl={intl}
                isNewListing={isNewListing}
                reviewsInfo={reviewsInfo}
              />
            )}
          </div>
          <GeolocationInfo
            className={css.geolocationInfo}
            intl={intl}
            listing={currentListing}
            searchParams={searchParams}
          />
          <div className={css.inline}>
            {bookingDate && (
              <ListingPrice
                className={css.listingPrice}
                intl={intl}
                listing={listing}
                searchParams={searchParams}
              />
            )}
            {makeOfferButton}
          </div>
        </div>
        <FavoritesButton className={favoritesButtonClasses} listingId={listingUuid} />
        <div className={css.badgeContainer}>
          {instantBookingFrom && (
            <InstantBookingBadge className={css.instantBookingBadge} textOnly />
          )}
          {isHostedBySuperHost && <SuperhostBadge className={css.superhostBadge} />}
          {showLastMinuteBadge && (
            <LastMinuteBadge
              className={css.lastMinuteBadge}
              hours={minHoursBeforeBookingRequestAsNumber}
            />
          )}
        </div>
      </NamedLink>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  search: null,
  searchParams: {},
  to: null,
  setActiveListing: () => null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  search: object,
  searchParams: object,
  to: shape({ search: string }),

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

const mapStateToProps = (state, props) => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.Auth;
  const myFavorites = pathOr(
    [],
    ['attributes', 'profile', 'protectedData', 'myFavorites'],
    currentUser
  );
  const myCart = pathOr([], ['attributes', 'profile', 'publicData', 'addToCart'], currentUser);

  return {
    myCart,
    myFavorites,
    isAuthenticated,
  };
};

const ListingCard = compose(connect(mapStateToProps, null), injectIntl)(ListingCardComponent);

export default ListingCard;
